import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import UserService from "rc-app/services/UserService";
import { getAgencyById } from "rc-app/api/agencyActionsApi";
import { getCustomerList } from "rc-app/api/customerActionsApi";

interface AgencyOption {
  id: string;
  slug: string;
  name: string;
  logo: string;
}

const CustomerAgencySelect: React.FC = () => {
  const navigate = useNavigate();
  const [agencies, setAgencies] = useState<AgencyOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [imgErrors, setImgErrors] = useState<Set<string>>(new Set());

  useEffect(() => {
    const load = async () => {
      try {
        const cached = sessionStorage.getItem("customerAgencies");
        if (cached) {
          const parsed: AgencyOption[] = JSON.parse(cached);
          if (parsed.length === 1) {
            localStorage.setItem("agencySlug", parsed[0].slug);
            navigate(`/c/${parsed[0].slug}/p/library`, { replace: true });
            return;
          }
          setAgencies(parsed);
          setLoading(false);
          return;
        }

        const listRes = await getCustomerList(0, 100, "", "", "", UserService.getUseremail());
        const records: any[] = listRes?.re_customer_list || [];

        const agencyMap = new Map<string, AgencyOption>();
        for (const record of records) {
          const aId = record?.agency_id;
          if (aId && !agencyMap.has(aId)) {
            const agencyData = await getAgencyById(aId);
            if (agencyData?.slug) {
              agencyMap.set(aId, {
                id: aId,
                slug: agencyData.slug,
                name: agencyData.name || "",
                logo: agencyData.logo || "",
              });
            }
          }
        }

        const list = Array.from(agencyMap.values());
        sessionStorage.setItem("customerAgencies", JSON.stringify(list));

        if (list.length === 1) {
          localStorage.setItem("agencySlug", list[0].slug);
          navigate(`/c/${list[0].slug}/p/library`, { replace: true });
          return;
        }

        setAgencies(list);
      } catch (err) {
        console.error("Failed to load agencies:", err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [navigate]);

  const handleSelect = (agency: AgencyOption) => {
    localStorage.setItem("agencySlug", agency.slug);
    sessionStorage.setItem("customerAgencySelected", agency.slug);
    navigate(`/c/${agency.slug}/p/library`);
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <span className="inline-block w-8 h-8 rounded-full border-2 border-white/30 border-t-white animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col items-center justify-center p-6">
      <h1 className="text-white text-2xl md:text-3xl font-semibold mb-2 text-center">
        Select an Agency
      </h1>
      <p className="text-[#889DB2] mb-10 text-sm text-center">
        Choose the agency you want to view projects for
      </p>
      <div className="flex flex-wrap justify-center gap-6 w-full max-w-3xl">
        {agencies.map((agency) => (
          <button
            key={agency.id}
            onClick={() => handleSelect(agency)}
            className="w-52 bg-[#222F3C] border-2 border-white/10 hover:border-[#008564] rounded-2xl p-6 flex flex-col items-center gap-4 transition-all cursor-pointer"
          >
            {agency.logo && !imgErrors.has(agency.id) ? (
              <div className="w-28 h-16 bg-white rounded-xl overflow-hidden flex items-center justify-center">
                <img
                  src={agency.logo}
                  alt={agency.name}
                  className="w-full h-full object-contain"
                  onError={() =>
                    setImgErrors((prev) => new Set(prev).add(agency.id))
                  }
                />
              </div>
            ) : (
              <div className="w-28 h-16 bg-[#2E4051] rounded-xl flex items-center justify-center text-[#889DB2] text-2xl font-bold">
                {agency.name?.[0]?.toUpperCase() || "A"}
              </div>
            )}
            <span className="text-white font-medium text-center">
              {agency.name}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default CustomerAgencySelect;
